import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Avatar,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Link as MuiLink,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FolderIcon from "@mui/icons-material/Folder";
import CampaignIcon from "@mui/icons-material/Campaign";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import moment from "moment";
import "moment/locale/es";
import ENV from "../../config/env";
import {
  splitTituloContenido,
  esVideoNoticia,
  esImagenNoticia,
  extraerYoutubeId,
  textoParaMostrar,
  extraerPlayStore,
} from "../../utils/noticiasHelpers";
import YoutubeEmbed from "./YoutubeEmbed";
import PlayStoreCard from "./PlayStoreCard";

const firstChar = (s) => (s ? s.charAt(0).toUpperCase() : "?");

const avatarSrc = (profilePicture) =>
  profilePicture
    ? profilePicture.startsWith("http")
      ? profilePicture
      : `${ENV.API_BASE_URL}${profilePicture}`
    : undefined;

// Qué ilustra la portada de una noticia sin video ni imagen: su adjunto, su carpeta de documentos o un anuncio simple.
const tipoSinMedia = (noticia) => {
  const url = String(noticia.archivo_url || "").toLowerCase();
  if (url.endsWith(".pdf")) return { Icono: PictureAsPdfIcon, etiqueta: "PDF adjunto", color: "error" };
  if (url) return { Icono: DescriptionIcon, etiqueta: "Archivo adjunto", color: "warning" };
  if (noticia.documento_carpeta_id) return { Icono: FolderIcon, etiqueta: "Documentos", color: "success" };
  return { Icono: CampaignIcon, etiqueta: "Anuncio", color: "primary" };
};

// Portada ilustrada: fondo suave del color del tipo con una trama de puntos y el ícono al centro.
function PortadaIlustrada({ noticia, destacada }) {
  const { Icono, etiqueta, color } = tipoSinMedia(noticia);
  return (
    <Box
      component={RouterLink}
      to={`/noticias/${noticia.id}`}
      aria-label={`Abrir noticia (${etiqueta})`}
      sx={(theme) => {
        const c = theme.palette[color].main;
        return {
          display: "grid",
          placeItems: "center",
          aspectRatio: "16 / 9",
          height: destacada ? { md: "100%" } : undefined,
          minHeight: destacada ? { md: 280 } : undefined,
          textDecoration: "none",
          backgroundColor: alpha(c, theme.palette.mode === "dark" ? 0.16 : 0.08),
          backgroundImage: `radial-gradient(${alpha(c, 0.22)} 1px, transparent 1px)`,
          backgroundSize: "14px 14px",
        };
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <Box
          sx={(theme) => ({
            width: destacada ? 76 : 60,
            height: destacada ? 76 : 60,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "background.paper",
            color: `${color}.main`,
            boxShadow: `0 0 0 6px ${alpha(theme.palette[color].main, 0.12)}`,
            transition: "transform 0.25s ease",
            ".MuiCard-root:hover &": { transform: "scale(1.06)" },
          })}
        >
          <Icono sx={{ fontSize: destacada ? 38 : 30 }} />
        </Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: `${color}.main` }}
        >
          {etiqueta}
        </Typography>
      </Stack>
    </Box>
  );
}

// Portada: video de YouTube, video subido, imagen adjunta o, si no hay nada de eso, la portada ilustrada.
function Portada({ noticia, youtubeId, destacada }) {
  const url = noticia.archivo_url;

  if (youtubeId) {
    return <YoutubeEmbed videoId={youtubeId} sx={{ borderRadius: 0 }} />;
  }
  if (esVideoNoticia(url)) {
    return (
      <Box
        component="video"
        src={`${ENV.API_BASE_URL}${url}`}
        controls
        preload="metadata"
        playsInline
        sx={{ display: "block", width: "100%", aspectRatio: "16 / 9", bgcolor: "common.black" }}
      />
    );
  }
  if (esImagenNoticia(url)) {
    return (
      <Box
        component={RouterLink}
        to={`/noticias/${noticia.id}`}
        sx={{ display: "block", aspectRatio: "16 / 9", overflow: "hidden", bgcolor: "action.hover" }}
      >
        <Box
          component="img"
          src={`${ENV.API_BASE_URL}${url}`}
          alt=""
          loading="lazy"
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.4s ease",
            ".MuiCard-root:hover &": { transform: "scale(1.04)" },
          }}
        />
      </Box>
    );
  }
  return <PortadaIlustrada noticia={noticia} destacada={destacada} />;
}

export default function NoticiaCard({ noticia, onEdit, onDelete, destacada = false }) {
  const { titulo, cuerpo } = splitTituloContenido(noticia);
  const youtubeId = extraerYoutubeId(cuerpo);
  const texto = textoParaMostrar(cuerpo);
  const playStore = extraerPlayStore(cuerpo);
  // En español aunque se entre a /noticias sin pasar por el Dashboard (que es donde se fija el idioma global).
  const fecha = noticia.fecha ? moment(noticia.fecha).locale("es") : null;
  const tieneVideo = Boolean(youtubeId) || esVideoNoticia(noticia.archivo_url);
  const adjuntoDescargable =
    noticia.archivo_url && !esVideoNoticia(noticia.archivo_url) && !esImagenNoticia(noticia.archivo_url);

  return (
    <Card
      variant="outlined"
      component="article"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: destacada ? { xs: "column", md: "row" } : "column",
        borderRadius: 3,
        overflow: "hidden",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: 6, borderColor: "transparent" },
        "@media (prefers-reduced-motion: reduce)": { "&:hover": { transform: "none" } },
      }}
    >
      <Box sx={destacada ? { flex: { md: "0 0 56%" }, alignSelf: { md: "stretch" }, display: { md: "flex" }, flexDirection: "column", justifyContent: "center", bgcolor: tieneVideo ? "common.black" : undefined } : undefined}>
        <Portada noticia={noticia} youtubeId={youtubeId} destacada={destacada} />
      </Box>

      <Box sx={{ p: destacada ? { xs: 2.25, md: 3.5 } : 2.25, display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        {/* Fecha y sucursal */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Tooltip title={fecha ? fecha.format("DD/MM/YYYY HH:mm") : ""}>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}
            >
              {fecha ? `${fecha.format("D MMM YYYY").replace(".", "")} · ${fecha.fromNow()}` : ""}
            </Typography>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {tieneVideo && (
            <Tooltip title="Incluye video">
              <PlayCircleOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} aria-label="Incluye video" />
            </Tooltip>
          )}
          <Chip size="small" variant="outlined" label={noticia.sucursal || "—"} sx={{ height: 22, fontSize: 11.5 }} />
        </Stack>

        {destacada && (
          <Typography
            variant="overline"
            color="primary"
            sx={{ fontWeight: 800, lineHeight: 1.6, letterSpacing: "0.1em" }}
          >
            Más reciente
          </Typography>
        )}

        <MuiLink
          component={RouterLink}
          to={`/noticias/${noticia.id}`}
          underline="none"
          color="text.primary"
          sx={{
            fontWeight: 800,
            fontSize: destacada ? { xs: "1.2rem", md: "1.6rem" } : "1.05rem",
            lineHeight: 1.3,
            mb: 0.75,
            textWrap: "balance",
            display: "-webkit-box",
            WebkitLineClamp: destacada ? 3 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            transition: "color 0.15s ease",
            "&:hover": { color: "primary.main" },
          }}
        >
          {titulo || "Sin título"}
        </MuiLink>

        {texto && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: destacada ? 5 : 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
              mb: 1.5,
            }}
          >
            {texto}
          </Typography>
        )}

        {playStore && <PlayStoreCard {...playStore} compacta sx={{ mb: 1.5 }} />}

        {(noticia.documento_carpeta_id || adjuntoDescargable) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {noticia.documento_carpeta_id && (
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                icon={<FolderIcon />}
                label={noticia.documento_carpeta_nombre || "Ver documentos"}
                component={RouterLink}
                to={`/documentos/${noticia.documento_carpeta_id}`}
                clickable
              />
            )}
            {adjuntoDescargable && (
              <Chip
                size="small"
                variant="outlined"
                icon={<AttachFileIcon />}
                label="Descargar adjunto"
                component="a"
                href={`${ENV.API_BASE_URL}${noticia.archivo_url}`}
                target="_blank"
                rel="noopener noreferrer"
                clickable
              />
            )}
          </Stack>
        )}

        {/* Autor al pie; acciones de gestión a la derecha */}
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: "auto", pt: 1.5 }}>
          <Avatar
            src={avatarSrc(noticia.profile_picture)}
            sx={{ width: 28, height: 28, fontSize: "0.8rem", bgcolor: "action.selected", color: "text.secondary" }}
          >
            {firstChar(noticia.usuario)}
          </Avatar>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            Por <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{noticia.usuario || "Usuario"}</Box>
          </Typography>
          {onEdit && (
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEdit(noticia)} aria-label="Editar noticia">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={() => onDelete(noticia)}
                aria-label="Eliminar noticia"
                sx={{ "&:hover": { color: "error.main" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Card>
  );
}
